import { TaxStrategy } from '@principle-theorem/accounting';
import { ITranslationMap } from '@principle-theorem/practice-migrations/interfaces';
import { stafferToNamedDoc } from '@principle-theorem/principle-core';
import {
  IServiceCodeLineItem,
  IStaffer,
  ITreatmentBasePriceLineItem,
  ITreatmentConfiguration,
  ITreatmentLineItem,
  ITreatmentRef,
  InvoiceLineItemType,
  type ICustomLineItem,
} from '@principle-theorem/principle-core/interfaces';
import { WithPrimaryKey, WithRef } from '@principle-theorem/shared';
import { resolveMappedCodeTreatmentPair } from '../../../../mappings/item-codes';
import { ItemCodeResourceMapType } from '../../../../mappings/item-codes-to-xlsx';
import { TranslationMapHandler } from '../../../../translation-map';
import { type IExactV2InvoiceLine } from '../../../source/entities/patient-invoice-lines-v2';
import { type IExactV2Transaction } from '../../../source/entities/patient-transactions-v2';
import { exactToothConverter } from '../../../util/tooth';
import { resolveExactStaffer } from '../../mappings/staff';
import { ExactTransactionLedger } from './exact-transaction-ledger';

/**
 * Exact bills at two levels: a charge row carrying the money, and an optional
 * line group carrying the service breakdown behind it.
 */
export class ExactLineItems {
  constructor(
    private _ledger: ExactTransactionLedger,
    private _sourceItemCodes: WithPrimaryKey<
      ITranslationMap<object, ItemCodeResourceMapType>
    >[],
    private _sourceTreatmentMappings: WithPrimaryKey<
      ITranslationMap<ITreatmentConfiguration>
    >[],
    private _staff: WithPrimaryKey<ITranslationMap<IStaffer, unknown>>[]
  ) {}

  async build(
    charges: IExactV2Transaction[],
    translationMap: TranslationMapHandler
  ): Promise<(ITreatmentLineItem | ICustomLineItem)[]> {
    const lineItems: (ITreatmentLineItem | ICustomLineItem)[] = [];

    for (const charge of charges) {
      if (ExactTransactionLedger.isInvoiceSideAdjustment(charge)) {
        lineItems.push({
          uid: charge.id,
          type: InvoiceLineItemType.Fee,
          description: charge.adjustmentLabel ?? `Adjustment ${charge.id}`,
          amount: charge.amount,
          tax: 0,
          taxStatus: TaxStrategy.GSTApplicable,
          quantity: 1,
        });
        continue;
      }

      const matchingLines = ExactTransactionLedger.isTransfer(charge)
        ? []
        : this._ledger.linesForGroup(charge);

      if (!matchingLines.length) {
        lineItems.push(this._buildFallbackFeeLine(charge));
        continue;
      }

      const [serviceCodeItems, treatmentLineItems] =
        await this._buildBreakdownItems(
          this._priceLinesAgainstCharge(matchingLines, charge),
          translationMap
        );
      lineItems.push(...serviceCodeItems, ...treatmentLineItems);
    }

    return lineItems;
  }

  private _buildFallbackFeeLine(charge: IExactV2Transaction): ICustomLineItem {
    return {
      uid: charge.id,
      type: InvoiceLineItemType.Fee,
      description: charge.reference ?? `Charge ${charge.id}`,
      amount: charge.amount,
      tax: charge.gstAmount ?? 0,
      taxStatus: TaxStrategy.GSTApplicable,
      quantity: 1,
    };
  }

  /**
   * Exact records agreement-funded work twice: a zero-value shell holding the
   * breakdown, and a valued payor row holding none. Both merge into one
   * invoice, so the shell keeps its lines at nil.
   */
  private _priceLinesAgainstCharge(
    lines: IExactV2InvoiceLine[],
    charge: IExactV2Transaction
  ): IExactV2InvoiceLine[] {
    if (charge.amount !== 0) {
      return lines;
    }
    return lines.map((line) => ({
      ...line,
      patientAmount: 0,
      patientGst: 0,
    }));
  }

  private async _buildBreakdownItems(
    lines: IExactV2InvoiceLine[],
    translationMap: TranslationMapHandler
  ): Promise<[IServiceCodeLineItem[], ITreatmentLineItem[]]> {
    const serviceCodeItems: IServiceCodeLineItem[] = [];
    const treatmentLineItems: ITreatmentLineItem[] = [];

    for (const line of lines) {
      const serviceCode = line.service ?? line.adaCode ?? '';
      const [code, mappedTreatment] = resolveMappedCodeTreatmentPair(
        this._sourceItemCodes,
        this._sourceTreatmentMappings,
        serviceCode,
        serviceCode
      );

      if (code) {
        serviceCodeItems.push(
          this._buildServiceCodeItem(line, code.code.toString(), serviceCode)
        );
        continue;
      }

      const staffer =
        mappedTreatment && line.dentist
          ? await resolveExactStaffer(line.dentist, translationMap, this._staff)
          : undefined;

      if (!staffer) {
        serviceCodeItems.push(
          this._buildServiceCodeItem(line, serviceCode, serviceCode)
        );
        continue;
      }

      treatmentLineItems.push(
        this._buildTreatmentItem(line, serviceCode, staffer)
      );
    }

    return [serviceCodeItems, treatmentLineItems];
  }

  private _buildServiceCodeItem(
    line: IExactV2InvoiceLine,
    code: string,
    sourceCode: string
  ): IServiceCodeLineItem {
    return {
      uid: line.id,
      type: InvoiceLineItemType.ServiceCode,
      code,
      toothId: line.toothNumber
        ? exactToothConverter(line.toothNumber.toString())
        : undefined,
      description: `${code} - ${line.userComment ?? sourceCode}`,
      amount: this._lineGross(line),
      tax: line.patientGst ?? 0,
      taxStatus: TaxStrategy.GSTApplicable,
      quantity: 1,
    };
  }

  private _buildTreatmentItem(
    line: IExactV2InvoiceLine,
    sourceCode: string,
    staffer: WithRef<IStaffer>
  ): ITreatmentLineItem {
    const treatmentRef: ITreatmentRef = {
      treatmentUuid: line.id,
      attributedTo: stafferToNamedDoc(staffer),
    };
    const basePriceItem: ITreatmentBasePriceLineItem = {
      uid: line.id,
      type: InvoiceLineItemType.TreatmentBasePrice,
      treatmentRef,
      description: 'Base Price',
      amount: this._lineGross(line),
      tax: line.patientGst ?? 0,
      taxStatus: TaxStrategy.GSTApplicable,
      quantity: 1,
    };

    return {
      uid: line.id,
      type: InvoiceLineItemType.Treatment,
      treatmentRef,
      items: [basePriceItem],
      description: `${sourceCode} - ${line.userComment ?? sourceCode}`,
      amount: this._lineGross(line),
      tax: line.patientGst ?? 0,
      taxStatus: TaxStrategy.GSTApplicable,
      quantity: 1,
    };
  }

  /** Exact prices GST-exclusive, Principle line items are GST-inclusive. */
  private _lineGross(line: IExactV2InvoiceLine): number {
    return (line.patientAmount ?? 0) + (line.patientGst ?? 0);
  }
}
